/**
 * Logger Service
 *
 * Centralized logging utility for the application.
 * Automatically suppresses debug logs in production.
 */

type LogLevel = "info" | "warn" | "error" | "debug";

class Logger {
  private isDev: boolean;

  constructor() {
    this.isDev = import.meta.env.DEV;
  }

  /**
   * Log informational messages (only in development)
   */
  info(message: string, ...args: unknown[]): void {
    if (this.isDev) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  /**
   * Log warning messages (only in development)
   */
  warn(message: string, ...args: unknown[]): void {
    if (this.isDev) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  /**
   * Log error messages (always logs, even in production)
   */
  error(message: string, ...args: unknown[]): void {
    console.error(`[ERROR] ${message}`, ...args);
  }

  /**
   * Log debug messages (only in development)
   */
  debug(message: string, ...args: unknown[]): void {
    if (this.isDev) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Generic log method with level parameter
   */
  log(level: LogLevel, message: string, ...args: unknown[]): void {
    switch (level) {
      case "info":
        this.info(message, ...args);
        break;
      case "warn":
        this.warn(message, ...args);
        break;
      case "error":
        this.error(message, ...args);
        break;
      case "debug":
        this.debug(message, ...args);
        break;
    }
  }
}

export const logger = new Logger();
